type ErrorReporterFn = (error: unknown, context?: Record<string, unknown>) => void;
type BreadcrumbReporterFn = (message: string, data?: Record<string, unknown>) => void;
type TagReporterFn = (key: string, value: string) => void;
export type SpanAttributes = Record<string, string | number | boolean>;
export type SpanOptions = { name: string; op: string; attributes?: SpanAttributes };
type SpanReporterFn = <T>(options: SpanOptions, fn: () => Promise<T>) => Promise<T>;

// Use globalThis with Symbol.for() to ensure a single shared instance across
// Next.js bundles (instrumentation vs route handlers), which each get their
// own copy of module-scoped variables.
const ERROR_REPORTER_KEY = Symbol.for('redcrawl.errorReporter');
const BREADCRUMB_REPORTER_KEY = Symbol.for('redcrawl.breadcrumbReporter');
const TAG_REPORTER_KEY = Symbol.for('redcrawl.tagReporter');
const SPAN_REPORTER_KEY = Symbol.for('redcrawl.spanReporter');

type GlobalReporters = typeof globalThis & {
  [key: symbol]:
    | ErrorReporterFn
    | BreadcrumbReporterFn
    | TagReporterFn
    | SpanReporterFn
    | undefined;
};

const g = globalThis as GlobalReporters;

export const setErrorReporter = (fn: ErrorReporterFn) => {
  g[ERROR_REPORTER_KEY] = fn;
};

export const reportError = (error: unknown, context?: Record<string, unknown>) => {
  (g[ERROR_REPORTER_KEY] as ErrorReporterFn | undefined)?.(error, context);
};

export const setBreadcrumbReporter = (fn: BreadcrumbReporterFn) => {
  g[BREADCRUMB_REPORTER_KEY] = fn;
};

export const addBreadcrumb = (message: string, data?: Record<string, unknown>) => {
  (g[BREADCRUMB_REPORTER_KEY] as BreadcrumbReporterFn | undefined)?.(message, data);
};

export const setTagReporter = (fn: TagReporterFn) => {
  g[TAG_REPORTER_KEY] = fn;
};

export const setErrorTag = (key: string, value: string) => {
  (g[TAG_REPORTER_KEY] as TagReporterFn | undefined)?.(key, value);
};

export const setSpanReporter = (fn: SpanReporterFn) => {
  g[SPAN_REPORTER_KEY] = fn;
};

// Times `fn` as a child span of the active trace; without a reporter it just runs `fn`.
export const withSpan = <T>(options: SpanOptions, fn: () => Promise<T>): Promise<T> => {
  const reporter = g[SPAN_REPORTER_KEY] as SpanReporterFn | undefined;
  return reporter ? reporter(options, fn) : fn();
};
